/**
 * HUD & Telemetry Interface Controller.
 * Binds UI controls, renders dynamic LOD buttons, updates reduction stats cards,
 * wireframe toggles, color pickers, view mode tabs, independent IBL and Emissive brightness sliders,
 * and material inspection dropdown.
 */

export class UIController {
  constructor(app) {
    this.app = app;

    // DOM Elements
    this.modelSelect = document.getElementById('modelSelect');
    this.modelName = document.getElementById('modelName');
    this.modelDesc = document.getElementById('modelDesc');
    this.modelAttribution = document.getElementById('modelAttribution');
    this.modelFormatBadge = document.getElementById('modelFormatBadge');
    
    this.lodCountBadge = document.getElementById('lodCountBadge');
    this.lodLevelContainer = document.getElementById('lodLevelContainer');

    this.reductionBadge = document.getElementById('reductionBadge');
    this.reductionBar = document.getElementById('reductionBar');
    
    this.triCount = document.getElementById('triCount');
    this.triDiff = document.getElementById('triDiff');
    this.vertCount = document.getElementById('vertCount');
    this.vertDiff = document.getElementById('vertDiff');
    this.memEstimate = document.getElementById('memEstimate');

    this.shadingInspectorSelect = document.getElementById('shadingInspectorSelect');

    this.wireframeToggle = document.getElementById('wireframeToggle');
    this.wireframeColorPicker = document.getElementById('wireframeColorPicker');

    this.lightingSelect = document.getElementById('lightingSelect');
    this.exposureSlider = document.getElementById('exposureSlider');
    this.exposureVal = document.getElementById('exposureVal');

    this.emissiveSlider = document.getElementById('emissiveSlider');
    this.emissiveVal = document.getElementById('emissiveVal');

    this.gridToggleBtn = document.getElementById('gridToggleBtn');
    this.autoRotateBtn = document.getElementById('autoRotateBtn');
    this.autoRotateLightBtn = document.getElementById('autoRotateLightBtn');
    this.lightFollowCamBtn = document.getElementById('lightFollowCamBtn');
    this.resetCamBtn = document.getElementById('resetCamBtn');



    this.rightLodPanel = document.getElementById('rightLodPanel');
    this.rightLodGrid = document.getElementById('rightLodGrid');
    this.hudDistanceHeader = document.getElementById('hudDistanceHeader');
    this.distanceHud = this.rightLodPanel; // Map distanceHud to rightLodPanel for backward compatibility
    this.activeLodIndicator = document.getElementById('activeLodIndicator');
    this.distanceVal = document.getElementById('distanceVal');
    this.distanceSlider = document.getElementById('distanceSlider');
    this.distanceThresholdsContainer = document.getElementById('distanceThresholdsContainer');
    this.resetDistancesBtn = document.getElementById('resetDistancesBtn');

    // Mobile Sidebar Elements
    this.menuToggleBtn = document.getElementById('menuToggleBtn');
    this.sidebarCloseBtn = document.getElementById('sidebarCloseBtn');
    this.sidebarOverlay = document.getElementById('sidebarOverlay');
    this.sidebarLeft = document.querySelector('.sidebar-left');

    this.bindEvents();
  }

  closeSidebarOnMobile() {
    if (window.innerWidth <= 1024) {
      if (this.sidebarLeft) this.sidebarLeft.classList.remove('open');
      if (this.sidebarOverlay) this.sidebarOverlay.classList.remove('active');
    }
  }

  bindEvents() {
    // Mobile Sidebar Toggles
    if (this.menuToggleBtn) {
      this.menuToggleBtn.addEventListener('click', () => {
        if (this.sidebarLeft) this.sidebarLeft.classList.add('open');
        if (this.sidebarOverlay) this.sidebarOverlay.classList.add('active');
      });
    }

    if (this.sidebarCloseBtn) {
      this.sidebarCloseBtn.addEventListener('click', () => {
        this.closeSidebarOnMobile();
      });
    }

    if (this.sidebarOverlay) {
      this.sidebarOverlay.addEventListener('click', () => {
        this.closeSidebarOnMobile();
      });
    }

    // Model Select
    if (this.modelSelect) {
      this.modelSelect.addEventListener('change', (e) => {
        this.app.selectModel(e.target.value);
        this.closeSidebarOnMobile();
      });
    }

    // Viewing Mode Tabs (Single, Split, Distance)
    const modeTabs = document.querySelectorAll('.mode-tab');
    modeTabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const mode = tab.dataset.mode;
        // Sync active class across all tabs matching this mode
        modeTabs.forEach(t => {
          if (t.dataset.mode === mode) {
            t.classList.add('active');
          } else {
            t.classList.remove('active');
          }
        });
        this.app.setViewingMode(mode);
        this.closeSidebarOnMobile();
      });
    });

    // Material & Texture Inspection Select
    if (this.shadingInspectorSelect) {
      this.shadingInspectorSelect.addEventListener('change', (e) => {
        this.app.setShadingMode(e.target.value);
        this.closeSidebarOnMobile();
      });
    }

    // Wireframe Toggles
    if (this.wireframeToggle) {
      this.wireframeToggle.addEventListener('click', () => {
        const isActive = this.wireframeToggle.classList.toggle('active');
        this.app.toggleWireframeOverlay(isActive);
      });
    }

    if (this.wireframeColorPicker) {
      this.wireframeColorPicker.addEventListener('input', (e) => {
        this.app.setWireframeColor(e.target.value);
      });
    }

    // Lighting Preset
    if (this.lightingSelect) {
      this.lightingSelect.addEventListener('change', (e) => {
        this.app.setLightingPreset(e.target.value);
      });
    }

    // IBL Environment Brightness Slider
    if (this.exposureSlider) {
      this.exposureSlider.addEventListener('input', (e) => {
        const val = parseFloat(e.target.value);
        if (this.exposureVal) this.exposureVal.textContent = `${val.toFixed(2)}x`;
        this.app.setExposure(val);
      });
    }

    // Independent Emissive Glow Brightness Slider
    if (this.emissiveSlider) {
      this.emissiveSlider.addEventListener('input', (e) => {
        const val = parseFloat(e.target.value);
        if (this.emissiveVal) this.emissiveVal.textContent = `${val.toFixed(1)}x`;
        this.app.setEmissiveIntensity(val);
      });
    }

    // Grid Floor Toggle
    if (this.gridToggleBtn) {
      this.gridToggleBtn.addEventListener('click', () => {
        const isActive = this.gridToggleBtn.classList.toggle('active');
        this.app.setGridVisible(isActive);
      });
    }

    // Auto Rotate Toggle
    if (this.autoRotateBtn) {
      this.autoRotateBtn.addEventListener('click', () => {
        const isActive = this.autoRotateBtn.classList.toggle('active');
        this.app.setAutoRotate(isActive);
      });
    }

    // Auto Rotate Light Toggle
    if (this.autoRotateLightBtn) {
      this.autoRotateLightBtn.addEventListener('click', () => {
        const isActive = this.autoRotateLightBtn.classList.toggle('active');
        this.app.setAutoRotateLight(isActive);
      });
    }

    // Light Follows Camera (Spotlight Mode) Toggle
    if (this.lightFollowCamBtn) {
      this.lightFollowCamBtn.addEventListener('click', () => {
        const isActive = this.lightFollowCamBtn.classList.toggle('active');
        this.app.setLightFollowsCamera(isActive);
      });
    }

    // Reset Camera Button
    if (this.resetCamBtn) {
      this.resetCamBtn.addEventListener('click', () => {
        this.app.resetCamera();
      });
    }



    // Distance Slider in Auto-Distance Mode
    if (this.distanceSlider) {
      this.distanceSlider.addEventListener('input', (e) => {
        const dist = parseFloat(e.target.value);
        if (this.distanceVal) {
          const formatted = dist < 2.0 ? dist.toFixed(2) : dist.toFixed(1);
          this.distanceVal.textContent = `${formatted} m`;
        }
        this.app.onDistanceSliderChange(dist);
      });
    }

    // Reset Distances Defaults Button
    if (this.resetDistancesBtn) {
      this.resetDistancesBtn.addEventListener('click', () => {
        this.app.resetDistanceThresholds();
      });
    }
  }

  updateInspectorDropdownOptions(textureModes) {
    if (!this.shadingInspectorSelect || !Array.isArray(textureModes)) return;
    this.shadingInspectorSelect.innerHTML = '';

    textureModes.forEach(mode => {
      const opt = document.createElement('option');
      opt.value = mode.key;
      opt.textContent = mode.label;
      this.shadingInspectorSelect.appendChild(opt);
    });
  }

  populateModelDropdown(models) {
    if (!this.modelSelect) return;
    this.modelSelect.innerHTML = '';
    models.forEach(model => {
      const opt = document.createElement('option');
      opt.value = model.id;
      opt.textContent = model.name;
      this.modelSelect.appendChild(opt);
    });
  }

  updateModelHeaderInfo(modelConfig) {
    if (this.modelName) this.modelName.textContent = modelConfig.name || 'Showcase Model';
    if (this.modelDesc) this.modelDesc.textContent = modelConfig.description || 'Pre-processed LOD demonstration model.';
    if (this.modelFormatBadge) {
      this.modelFormatBadge.textContent = modelConfig.type === 'single-file' ? 'SINGLE GLB CONTAINER' : (modelConfig.type === 'procedural' ? 'PROCEDURAL GENERATED' : 'MULTI-FILE SET');
    }

    if (this.modelAttribution) {
      if (modelConfig.attribution) {
        const title = modelConfig.name || 'Model';
        const author = modelConfig.attribution.author || 'Unknown';
        const license = modelConfig.attribution.license || 'CC BY 4.0';
        const url = modelConfig.attribution.url || '#';
        const licenseUrl = modelConfig.attribution.licenseUrl || 'http://creativecommons.org/licenses/by/4.0/';

        this.modelAttribution.innerHTML = `"<a href="${url}" target="_blank" rel="noopener noreferrer">${title}</a>" by ${author} is licensed under <a href="${licenseUrl}" target="_blank" rel="noopener noreferrer">${license}</a>.`;
        this.modelAttribution.style.display = 'block';
      } else {
        this.modelAttribution.innerHTML = '';
        this.modelAttribution.style.display = 'none';
      }
    }
  }

  renderLodButtons(availableLevels, activeLevel) {
    this.availableLevelsCache = availableLevels;
    this.activeLevelCache = activeLevel;
    this.refreshRightLodGrid();
  }

  setActiveLodButton(activeLevel) {
    this.activeLevelCache = activeLevel;
    
    // Update the floating indicator badge for Auto-Distance mode
    if (this.activeLodIndicator) {
      this.activeLodIndicator.textContent = `LOD ${activeLevel}`;
    }

    // Update active class on the squares
    if (this.rightLodGrid) {
      const squares = this.rightLodGrid.querySelectorAll('.lod-square');
      squares.forEach(sq => {
        if (parseInt(sq.dataset.level, 10) === activeLevel) {
          sq.classList.add('active');
        } else {
          sq.classList.remove('active');
        }
      });
    }
  }

  updateDistanceSliderBounds(thresholds) {
    if (!this.distanceSlider || !thresholds || thresholds.length === 0) return;
    
    const minVal = parseFloat((thresholds[0] !== undefined ? thresholds[0] : 0).toFixed(3));
    const lastVal = thresholds[thresholds.length - 1] !== undefined ? thresholds[thresholds.length - 1] : 1.0;
    
    // Extend max distance by +20% beyond the final LOD activation threshold
    const maxVal = parseFloat(Math.max(lastVal * 1.2, lastVal + 0.05).toFixed(3));
    
    const span = Math.max(0.001, maxVal - minVal);
    const stepVal = parseFloat((span / 1000).toFixed(4));

    this.distanceSlider.min = minVal.toString();
    this.distanceSlider.max = maxVal.toString();
    this.distanceSlider.step = stepVal.toString();
  }

  renderDistanceInputs(availableLevels, thresholds, activeLevel) {
    this.availableLevelsCache = availableLevels;
    this.distanceThresholdsCache = thresholds;
    this.activeLevelCache = activeLevel;
    this.refreshRightLodGrid();
  }

  refreshRightLodGrid() {
    if (!this.rightLodGrid || !this.availableLevelsCache) return;
    this.rightLodGrid.innerHTML = '';

    const currentMode = this.app ? this.app.viewingMode : 'single';
    const thresholds = this.distanceThresholdsCache || [];
    const activeLevel = this.activeLevelCache !== undefined ? this.activeLevelCache : 0;

    this.availableLevelsCache.forEach((level, idx) => {
      const square = document.createElement('div');
      square.className = `lod-square ${level === activeLevel ? 'active' : ''}`;
      square.dataset.level = level;

      const title = document.createElement('span');
      title.className = 'lod-square-title';
      title.textContent = `L0${level}`;

      const value = document.createElement('span');
      value.className = 'lod-square-value';
      
      if (currentMode === 'distance') {
        const distVal = thresholds[idx] !== undefined ? thresholds[idx] : idx * 5;
        value.textContent = `${distVal.toFixed(1)}m`;
      } else {
        value.textContent = level === 0 ? 'Base' : `LOD ${level}`;
      }

      square.appendChild(title);
      square.appendChild(value);

      if (currentMode !== 'distance') {
        square.addEventListener('click', () => {
          this.setActiveLodButton(level);
          this.app.selectLodLevel(level);
        });
      }

      this.rightLodGrid.appendChild(square);
    });

    if (currentMode === 'distance' && this.distanceThresholdsCache) {
      this.updateDistanceSliderBounds(this.distanceThresholdsCache);
    }
  }

  updateSliderValue(distance) {
    if (this.distanceSlider) {
      this.distanceSlider.value = distance.toString();
    }
    if (this.distanceVal) {
      const formatted = distance < 2.0 ? distance.toFixed(2) : distance.toFixed(1);
      this.distanceVal.textContent = `${formatted} m`;
    }
  }

  updateTelemetry(telemetry) {
    if (this.reductionBadge) {
      this.reductionBadge.textContent = `-${telemetry.reductionPercent}%`;
    }

    if (this.reductionBar) {
      this.reductionBar.style.width = `${telemetry.reductionPercent}%`;
    }

    if (this.triCount) {
      this.triCount.textContent = telemetry.triangles.toLocaleString();
    }

    if (this.triDiff) {
      this.triDiff.textContent = `Base: ${telemetry.baseTriangles.toLocaleString()}`;
    }

    if (this.vertCount) {
      this.vertCount.textContent = telemetry.vertices.toLocaleString();
    }

    if (this.vertDiff) {
      this.vertDiff.textContent = `Base: ${telemetry.baseVertices.toLocaleString()}`;
    }

    if (this.memEstimate) {
      this.memEstimate.textContent = `${telemetry.memMb} MB`;
    }
  }

  toggleDistanceHud(visible) {
    if (this.rightLodPanel) {
      // The unified panel is always visible in all modes now
      this.rightLodPanel.classList.remove('hidden');
      
      const currentMode = this.app ? this.app.viewingMode : 'single';
      this.rightLodPanel.className = `right-lod-panel glass-panel mode-${currentMode}`;
    }

    if (this.hudDistanceHeader) {
      if (this.app && this.app.viewingMode === 'distance') {
        this.hudDistanceHeader.classList.remove('hidden');
      } else {
        this.hudDistanceHeader.classList.add('hidden');
      }
    }

    this.refreshRightLodGrid();
  }
}
